import { Timestamp } from '@bufbuild/protobuf/wkt'
import { useCallback, useMemo, useState } from 'react'

import { useListAssessorQuestionnaires } from '@/api/assessor-questionnaire.hook'
import { useCreateInfoQuestionnaire } from '@/api/create-questionnaire.hook'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { QuestionnaireTemplate } from '@/gen/questionnaire/v1/template_service_pb'
import { QuestionnaireType } from '@/gen/questionnaire/v2/model_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { useReportErrorsCallback } from '@/lib/connect/error-reporting'

import { useListQuestionnaireTemplates } from '@/pages/questionnaire-template/common/hooks/questionnaire-template.hook'

import { AddQuestionnaire } from '@/modals/add-questionnaire-modal/add-questionnaire'

import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

import { ADD_QUESTIONNAIRE_MODAL_LABELS } from './labels'

type AddQuestionnaireProps = {
  companyId: Company['id']
  ongoingAssessmentId: OngoingAssessment['id']
  source: 'tab-header' | 'empty-state' | 'manual-section'
}

export const useAddQuestionnaireModal = ({
  companyId,
  ongoingAssessmentId,
  source,
}: AddQuestionnaireProps) => {
  const reportErrors = useReportErrorsCallback()
  const { toast } = useToast()
  const trackQuestionnaireCreate = useTrackCallback('questionnaire.new')
  const trackQuestionnaireNewClick = useTrackCallback('questionnaire.new.click')

  const [open, setOpen] = useState(false)
  const [selectedTemplates, setSelectedTemplates] = useState<QuestionnaireTemplate[]>([])

  const { data: templates } = useListQuestionnaireTemplates()
  const { data } = useListAssessorQuestionnaires({ companyId, ongoingAssessmentId })

  const { mutateAsync, isPending } = useCreateInfoQuestionnaire()

  const questionnaires = useMemo(() => data?.questionnaires ?? [], [data])

  const unselectableTemplates = useMemo(() => {
    const selectedTemplatesIds = selectedTemplates.map((template) => template.id)

    return templates?.filter((template) => !selectedTemplatesIds.includes(template.id))
  }, [templates, selectedTemplates])

  const closeModal = useCallback(() => {
    setOpen(false)
    setSelectedTemplates([])
  }, [])

  const onSubmit = useCallback(async () => {
    try {
      const dueDate = questionnaires.reduce<Timestamp | undefined>((min, questionnaire) => {
        if (!questionnaire?.base?.dueDate) return min
        if (!min) return questionnaire.base?.dueDate
        return questionnaire.base?.dueDate.seconds < min.seconds ? questionnaire.base?.dueDate : min
      }, undefined)

      await Promise.all(
        selectedTemplates.map(async (template) => {
          return await mutateAsync(
            {
              companyId: companyId,
              name: template.name,
              description: template.description,
              dueDate,
              type: QuestionnaireType.INFO,
              assessmentId: ongoingAssessmentId,
              specificQuestionnaireRequest: {
                case: 'infoQuestionnaireRequest',
                value: {
                  templateId: template.id,
                },
              },
            },
            {
              onSuccess: () => {
                trackQuestionnaireCreate({
                  companyId,
                  assessmentId: ongoingAssessmentId,
                  templateId: template.id,
                  templateName: template.name,
                  source,
                })
              },
            },
          )
        }),
      )

      toast({
        variant: 'success',
        title: ADD_QUESTIONNAIRE_MODAL_LABELS.toasts.questionnaireCreated,
      })

      closeModal()
    } catch (e) {
      reportErrors(e as Error)
      toast({
        variant: 'destructive',
        title: ADD_QUESTIONNAIRE_MODAL_LABELS.toasts.createFailed,
      })
    }
  }, [
    companyId,
    ongoingAssessmentId,
    questionnaires,
    selectedTemplates,
    mutateAsync,
    reportErrors,
    toast,
    trackQuestionnaireCreate,
    closeModal,
    source,
  ])

  const renderModal = useCallback(
    () => (
      <Modal
        title={ADD_QUESTIONNAIRE_MODAL_LABELS.modalTitle}
        open={open}
        size='medium'
        onOk={onSubmit}
        onCancel={closeModal}
        confirmLoading={isPending}
        primaryButtonProps={{ disabled: !selectedTemplates.length }}
      >
        <AddQuestionnaire
          questionnaires={questionnaires}
          selectedTemplates={selectedTemplates}
          setSelectedTemplates={setSelectedTemplates}
          unselectableTemplates={unselectableTemplates}
        />
      </Modal>
    ),
    [
      open,
      questionnaires,
      selectedTemplates,
      unselectableTemplates,
      isPending,
      onSubmit,
      closeModal,
    ],
  )

  const openModal = useCallback(() => {
    trackQuestionnaireNewClick({ companyId, assessmentId: ongoingAssessmentId, source })
    setOpen(true)
  }, [companyId, ongoingAssessmentId, source, trackQuestionnaireNewClick])

  return { renderModal, openModal }
}
